import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import ComicCard from "@/components/base/ComicCard";
import Pagination from "@/components/base/Pagination";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { comicCategories, comics } from "@/mocks/comics";

const PAGE_SIZE = 9;
const authorOptions = Array.from(new Set(comics.map((c) => c.author)));
const statusOptions = ["全部", "连载", "完结"] as const;
const sortOptions = [
  { key: "updated", label: "最近更新" },
  { key: "rating", label: "评分最高" },
  { key: "chapter", label: "章节最多" },
] as const;

type SortKey = (typeof sortOptions)[number]["key"];

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get("cat") || "全部";
  const [author, setAuthor] = useState("全部");
  const [status, setStatus] = useState<(typeof statusOptions)[number]>("全部");
  const [sort, setSort] = useState<SortKey>("updated");
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const categoryDetail = comicCategories.find((c) => c.name === activeCat);
  const title = `妖精漫画-《${activeCat}》漫画列表`;
  const description = `妖精漫画提供丰富的《${activeCat}》妖精题材漫画浏览与筛选，帮您找到喜爱的作品。`;

  useDocumentMeta({
    title,
    description,
    keywords: `妖精漫画,${activeCat}漫画,妖精题材漫画,漫画列表,漫画筛选`,
  });

  useEffect(() => {
    setPage(1);
  }, [activeCat, author, status, sort]);

  const filtered = useMemo(() => {
    const list = comics.filter((c) => {
      const catOk = activeCat === "全部" || c.category === activeCat;
      const authorOk = author === "全部" || c.author === author;
      const statusOk = status === "全部" || c.status === status;
      return catOk && authorOk && statusOk;
    });
    return [...list].sort((a, b) => {
      if (sort === "rating") return b.rating - a.rating;
      if (sort === "chapter") return b.latestChapter - a.latestChapter;
      return b.updatedAt.localeCompare(a.updatedAt);
    });
  }, [activeCat, author, status, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetFilters = () => {
    setAuthor("全部");
    setStatus("全部");
    setSort("updated");
    setSearchParams({}, { replace: true });
  };

  const selectCategory = (name: string) => {
    const next = new URLSearchParams(searchParams);
    if (name === "全部") next.delete("cat");
    else next.set("cat", name);
    setSearchParams(next);
  };

  return (
    <div className="w-full">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb
            items={[
              { label: "首页", path: "/" },
              { label: "漫画分类", path: "/category" },
              { label: activeCat },
            ]}
          />
          <h1 className="mt-4 font-heading text-2xl text-foreground-950 md:text-4xl">
            {activeCat === "全部" ? "全部妖精漫画" : `${activeCat}漫画`}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            {categoryDetail ? categoryDetail.desc : "妖精漫画全站作品，按分类、作者与状态快速筛选，找到属于你的奇幻故事。"}
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        {/* 子分类导航 */}
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => selectCategory("全部")}
            className={`rounded-full border px-4 py-2 text-sm transition-colors cursor-pointer whitespace-nowrap ${
              activeCat === "全部"
                ? "border-primary-500 bg-primary-500 text-background-50"
                : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
            }`}
          >
            全部
          </button>
          {comicCategories.map((cat) => (
            <button
              key={cat.key}
              type="button"
              onClick={() => selectCategory(cat.name)}
              className={`flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                activeCat === cat.name
                  ? "border-primary-500 bg-primary-500 text-background-50"
                  : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
              }`}
            >
              <i className={cat.icon}></i>
              {cat.name}
            </button>
          ))}
        </div>

        {/* 筛选栏 */}
        <div className="rounded-lg border border-background-200 bg-background-50">
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className="flex w-full items-center justify-between px-5 py-4 text-sm text-foreground-900 cursor-pointer md:hidden"
          >
            <span className="flex items-center gap-2">
              <i className="ri-filter-3-line text-primary-600"></i>
              筛选与排序
            </span>
            <i className={`text-lg ${filtersOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}></i>
          </button>

          <div className={`${filtersOpen ? "block" : "hidden"} px-5 pb-5 md:block md:px-5 md:py-5`}>
            <div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-center md:gap-6">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500">作者</span>
                <select
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  className="h-9 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 cursor-pointer"
                >
                  <option value="全部">全部作者</option>
                  {authorOptions.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500">状态</span>
                <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-50 px-1 py-1">
                  {statusOptions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setStatus(s)}
                      className={`rounded-full px-3 py-1 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                        status === s
                          ? "bg-secondary-500 text-background-50"
                          : "text-foreground-600 hover:text-secondary-800"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500">排序</span>
                <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-50 px-1 py-1">
                  {sortOptions.map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setSort(opt.key)}
                      className={`rounded-full px-3 py-1 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                        sort === opt.key
                          ? "bg-primary-500 text-background-50"
                          : "text-foreground-600 hover:text-primary-600"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <p className="mt-4 text-sm text-foreground-500">
          共找到 <span className="text-primary-600">{filtered.length}</span> 部作品
        </p>

        {pageItems.length > 0 ? (
          <>
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {pageItems.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>

            <div className="mt-10">
              <Pagination current={page} total={totalPages} onChange={setPage} />
            </div>
          </>
        ) : (
          <div className="mt-10 flex flex-col items-center gap-4 rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-600">
              <i className="ri-emotion-sad-line text-3xl"></i>
            </span>
            <p className="text-base text-foreground-800">没有找到符合筛选条件的漫画</p>
            <p className="text-sm text-foreground-500">试试调整筛选条件，或重置后重新浏览吧。</p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-2 rounded-md bg-primary-500 px-6 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
            >
              重置筛选条件
            </button>
          </div>
        )}

        <div className="mt-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-primary-600 transition-colors hover:text-primary-700 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    </div>
  );
}